/**
 * APPLICATION:THEG_MOVIES_LIBRARY — mounts account-switch isolation on member change.
 * Listens for resolved member/project_ref props from the host (RUNYI assignment).
 * AGENT:GROK_BOT — Step4 2026-10-01
 */
"use client";

import { useEffect, useRef } from "react";
import {
  isolateOnMemberSwitch,
  writeThegRuntimeTarget,
  type ThegMemberRuntimeTarget,
} from "./theg-account-switch-isolation";

export type ThegAccountSwitchGuardProps = {
  memberKey?: string | null;
  projectRef?: string | null;
  assignmentSlot?: string | null;
  /** Optional callback after isolation runs (host may re-fetch tenant session). */
  onIsolated?: (previousProjectRef: string | null) => void;
};

export function ThegAccountSwitchGuard({
  memberKey = null,
  projectRef = null,
  assignmentSlot = null,
  onIsolated,
}: ThegAccountSwitchGuardProps) {
  const prevRef = useRef<{ memberKey: string | null; projectRef: string | null }>({
    memberKey: null,
    projectRef: null,
  });

  useEffect(() => {
    const previous = prevRef.current;
    const next = { memberKey: memberKey ?? null, projectRef: projectRef ?? null };
    const report = isolateOnMemberSwitch(previous, next);
    if (report) onIsolated?.(report.previousProjectRef);

    // Persist only RUNYI-resolved targets; null clears pointer after isolation.
    if (next.memberKey || next.projectRef) {
      const target: ThegMemberRuntimeTarget = {
        memberKey: next.memberKey,
        projectRef: next.projectRef,
        assignmentSlot: assignmentSlot ?? null,
        resolvedAt: new Date().toISOString(),
      };
      writeThegRuntimeTarget(target);
    }

    prevRef.current = next;
  }, [memberKey, projectRef, assignmentSlot, onIsolated]);

  return null;
}
